import { useEffect, useState } from 'react';
import { adminApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BLANK = {
  backend: 'local',
  s3_bucket: '',
  s3_region: '',
  s3_endpoint_url: '',
  s3_access_key_id: '',
  s3_secret_access_key: undefined, // undefined = leave whatever's stored unchanged
  s3_key_prefix: '',
};

const AdminInfrastructureStorage = () => {
  const [form, setForm] = useState(BLANK);
  const [secretSet, setSecretSet] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi.getStorageSettings();
      const data = res.data || {};
      setForm({
        backend: data.backend || 'local',
        s3_bucket: data.s3_bucket || '',
        s3_region: data.s3_region || '',
        s3_endpoint_url: data.s3_endpoint_url || '',
        s3_access_key_id: data.s3_access_key_id || '',
        s3_secret_access_key: undefined,
        s3_key_prefix: data.s3_key_prefix || '',
      });
      setSecretSet(!!data.s3_secret_access_key_set);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load storage settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await adminApi.testStorageSettings(form);
      setTestResult(res.data);
      if (res.data?.ok) toast.success(res.data.message || 'Connection successful');
      else toast.error(`Connection failed: ${res.data?.error || 'unknown error'}`);
    } catch (e) {
      const err = e?.response?.data?.detail || e?.message || 'Test failed';
      setTestResult({ ok: false, error: err });
      toast.error(`Test failed: ${err}`);
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await adminApi.updateStorageSettings(form);
      toast.success('Storage settings saved');
      setSecretSet(!!res.data?.s3_secret_access_key_set);
      setForm((prev) => ({ ...prev, s3_secret_access_key: undefined }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</p>;
  }

  return (
    <div className="space-y-6" data-testid="admin-infrastructure-storage">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-foreground">File storage</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Where encrypted document blobs are written and read from.
        </p>
      </div>

      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="space-y-2">
            <Label>Backend</Label>
            <Select value={form.backend} onValueChange={(v) => setForm((prev) => ({ ...prev, backend: v }))}>
              <SelectTrigger className="w-64"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="local">Local disk / mounted filesystem</SelectItem>
                <SelectItem value="s3">S3-compatible object storage</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Switching only affects documents written <em>after</em> the change — existing files
              don't move automatically. Test the connection before saving a switch to S3.
            </p>
          </div>

          {form.backend === 's3' && (
            <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-border">
              <div className="space-y-1.5">
                <Label>Bucket</Label>
                <Input value={form.s3_bucket} onChange={set('s3_bucket')} placeholder="my-precious-data-bucket" />
              </div>
              <div className="space-y-1.5">
                <Label>Region</Label>
                <Input value={form.s3_region} onChange={set('s3_region')} placeholder="eu-west-2" />
              </div>
              <div className="space-y-1.5">
                <Label>Endpoint URL (optional)</Label>
                <Input value={form.s3_endpoint_url} onChange={set('s3_endpoint_url')} placeholder="https://minio.internal:9000" />
                <p className="text-xs text-muted-foreground">Only needed for a non-AWS service (MinIO, Ceph RGW, ...).</p>
              </div>
              <div className="space-y-1.5">
                <Label>Key prefix (optional)</Label>
                <Input value={form.s3_key_prefix} onChange={set('s3_key_prefix')} placeholder="precious-data/" />
              </div>
              <div className="space-y-1.5">
                <Label>Access key ID (optional)</Label>
                <Input value={form.s3_access_key_id} onChange={set('s3_access_key_id')} placeholder="leave blank to use an IAM role" />
              </div>
              <div className="space-y-1.5">
                <Label>Secret access key {secretSet && form.s3_secret_access_key === undefined && '(already set — leave blank to keep it)'}</Label>
                <Input
                  type="password"
                  value={form.s3_secret_access_key ?? ''}
                  onChange={(e) => setForm((prev) => ({ ...prev, s3_secret_access_key: e.target.value }))}
                  placeholder={secretSet ? '••••••••' : 'leave blank to use an IAM role'}
                />
              </div>
            </div>
          )}

          {testResult && (
            <p className={`text-sm ${testResult.ok ? 'text-brand-ink' : 'text-breach-ink'}`} data-testid="storage-test-result">
              {testResult.ok ? (testResult.message || 'Connected.') : `Failed: ${testResult.error}`}
            </p>
          )}

          <div className="flex items-center gap-2 pt-2">
            {form.backend === 's3' && (
              <Button variant="outline" onClick={handleTest} disabled={testing}>
                {testing && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                Test connection
              </Button>
            )}
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              Save
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminInfrastructureStorage;
